// FacebookAnalytics.tsx - OPTIMIZADO Y MEJORADO
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card, Row, Col, Statistic, Alert, Typography, Space, Tag, Table, Progress,
  Skeleton, Button, Input, Layout, Descriptions, Empty, List, Divider, Tooltip,
  Popover, Select, Modal
} from "antd";
import {
  NumberOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
  BarChartOutlined,
  WarningOutlined,
  EditOutlined,
  BellOutlined
} from "@ant-design/icons";
import { ANALYTICS_API_URL } from "../../../constants/config";
import { getAndSanitize } from "../../../shared/utils/sanitization";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const { Sider, Content } = Layout;
const { Option } = Select;

interface FacebookEvent {
  service: string;
  type: string;
  success: boolean;
  error: string | null;
  errorDetails?: {
    reason?: 'DISABLED_BY_CONFIG' | 'CONFIG_CHECK_ERROR' | 'MISSING_USER' | 'MISSING_USER_CODE' | 'MISSING_USER_ID' | 'MISSING_AMOUNT' | 'HTTP_ERROR' | 'NETWORK_ERROR';
    statusCode?: number;
    statusText?: string;
    responseData?: any;
    responseHeaders?: any;
    requestUrl?: string;
    requestPayload?: any;
    requestHeaders?: any;
    axiosError?: string;
    stack?: string;
    [key: string]: any;
  };
  timestamp: string;
  response: any;
  responseStatus?: number;
}

interface UserRequest {
  id: string;
  requestNumber: string;
  amount: number;
  createdAt: string;
  processedAt: string | null;
  wasNotified: boolean;
  events: FacebookEvent[];
}

interface UserAnalytics {
  userId: string;
  username: string;
  code: string | null;
  accountName: string | null;
  totalRequests: number;
  notifiedRequests: number;
  failedNotifications: number;
  totalAmount: number;
  notifiedAmount: number;
  successRate: number;
  requests: UserRequest[];
}

interface RecentRequest {
  id: string;
  requestNumber: string;
  username: string;
  userCode: string;
  userId: string | null;
  panelUserId: string;
  amount: number;
  createdAt: string;
  processedAt: string;
  wasNotified: boolean;
  notificationSuccess: boolean;
  notificationError: string | null;
  notificationErrorDetails?: {
    reason?: 'DISABLED_BY_CONFIG' | 'CONFIG_CHECK_ERROR' | 'MISSING_USER' | 'MISSING_USER_CODE' | 'MISSING_USER_ID' | 'MISSING_AMOUNT' | 'HTTP_ERROR' | 'NETWORK_ERROR';
    statusCode?: number;
    statusText?: string;
    responseData?: any;
    responseHeaders?: any;
    requestUrl?: string;
    requestPayload?: any;
    requestHeaders?: any;
    axiosError?: string;
    stack?: string;
    [key: string]: any;
  };
  accountName: string;
  adminId?: string;
  eventType?: 'FirstPurchase' | 'SecondPurchase' | 'RepeatCustomer'; // NUEVO: EventType
}

interface FacebookAnalyticsData {
  summary: {
    totalRequests: number;
    notifiedRequests: number;
    failedNotifications: number;
    notificationRate: number;
    totalAmount: number;
    notifiedAmount: number;
    amountRate: number;
  };
  todayStats?: {
    usersCreatedToday: number;
    usersNotifiedToday: number;
    usersNotNotifiedToday: number;
    usersErrorToday: number;
    notificationRateToday: number;
  };
  trends: Array<{
    date: string;
    totalRequests: number;
    notifiedRequests: number;
    failedNotifications: number;
    totalAmount: number;
    notifiedAmount: number;
  }>;
  accountBreakdown: Array<{
    accountName: string;
    totalRequests: number;
    notifiedRequests: number;
    failedNotifications: number;
    totalAmount: number;
    notifiedAmount: number;
  }>;
  adminBreakdown?: Array<{
    adminId: string;
    totalRequests: number;
    notifiedRequests: number;
    failedNotifications: number;
    totalAmount: number;
    notifiedAmount: number;
  }>;
  errorAnalysis: {
    totalErrors: number;
    uniqueErrors: string[];
    errorFrequency: Record<string, number>;
  };
  eventTypeBreakdown?: Array<{
    eventType: 'FirstPurchase' | 'SecondPurchase' | 'RepeatCustomer';
    count: number;
    notified: number;
    failed: number;
    totalAmount: number;
    notificationRate: number;
  }>; // NUEVO: Desglose por eventType
  recentRequests: RecentRequest[];
  dateRange: {
    start: string;
    end: string;
  };
  appliedFilters?: {
    adminId: string;
    accountName: string;
    includeAllAdmins: boolean;
  };
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

interface UserAnalyticsData {
  users: UserAnalytics[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

interface FacebookAnalyticsProps {
  loading?: boolean;
}

const CONFIG = {
  REFRESH_INTERVAL: 60000, // Cambiado a 60 segundos para reducir re-renders
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 2000,
} as const;

// Helper para traducir razones de error
const getReasonLabel = (reason?: string): { text: string; color: string } => {
  switch (reason) {
    case 'DISABLED_BY_CONFIG':
      return { text: 'Deshabilitado en Configuración', color: 'default' };
    case 'CONFIG_CHECK_ERROR':
      return { text: 'Error al Verificar Configuración', color: 'warning' };
    case 'MISSING_USER':
      return { text: 'Usuario No Encontrado', color: 'error' };
    case 'MISSING_USER_CODE':
      return { text: 'Código de Usuario Faltante', color: 'error' };
    case 'MISSING_USER_ID':
      return { text: 'ID de Usuario Faltante', color: 'error' };
    case 'MISSING_AMOUNT':
      return { text: 'Monto No Determinado', color: 'error' };
    case 'HTTP_ERROR':
      return { text: 'Error HTTP del Servidor', color: 'error' };
    case 'NETWORK_ERROR':
      return { text: 'Error de Red/Timeout', color: 'error' };
    default:
      return { text: 'Error Desconocido', color: 'error' };
  }
};

// NUEVO: Helper para traducir eventType
const getEventTypeLabel = (eventType?: 'FirstPurchase' | 'SecondPurchase' | 'RepeatCustomer'): { text: string; color: string; icon: string } => {
  switch (eventType) {
    case 'FirstPurchase':
      return { text: 'Primera Carga', color: 'green', icon: '🎉' };
    case 'SecondPurchase':
      return { text: 'Segunda Carga', color: 'blue', icon: '🔄' };
    case 'RepeatCustomer':
      return { text: 'Cliente Recurrente', color: 'purple', icon: '⭐' };
    default:
      return { text: 'Sin Categoría', color: 'default', icon: '❓' };
  }
};

// Componente separado para el contenido expandido (para manejar hooks correctamente)
const ExpandedUserRequests: React.FC<{ record: UserAnalytics }> = ({ record }) => {
  const [expandedFilter, setExpandedFilter] = useState<string>("all");
  const [expandedPage, setExpandedPage] = useState(1);
  const [expandedPageSize, setExpandedPageSize] = useState(10);

  // Filtrar requests del usuario
  const filteredUserRequests = useMemo(() => {
    switch (expandedFilter) {
      case "notNotified":
        return record.requests.filter(r => !r.wasNotified);
      case "success":
        return record.requests.filter(r => {
          if (!r.wasNotified) return false;
          if (!r.events || r.events.length === 0) return false;
          const lastEvent = r.events[r.events.length - 1];
          return lastEvent.success === true;
        });
      case "error":
        return record.requests.filter(r => {
          if (!r.wasNotified) return false;
          if (!r.events || r.events.length === 0) return false;
          const lastEvent = r.events[r.events.length - 1];
          // Explícitamente verificar que el último evento falló
          return lastEvent.success === false;
        });
      default:
        return record.requests;
    }
  }, [record.requests, expandedFilter]);

  const requestColumns = [
    {
      title: 'Nº Solicitud',
      dataIndex: 'requestNumber',
      key: 'requestNumber',
      render: (text: string) => <Text code>{text}</Text>
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number | null) => amount != null ? `$${amount.toLocaleString()}` : '-'
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format("DD/MM/YYYY HH:mm")
    },
    {
      title: 'Estado',
      key: 'status',
      render: (record: UserRequest) => {
        // NUEVO: SIEMPRE debe haber eventos de notificación
        if (!record.events || record.events.length === 0) {
          return (
            <Tag color="warning" icon={<WarningOutlined />}>
              Sin Eventos (Error del Sistema)
            </Tag>
          );
        }

        const lastEvent = record.events[record.events.length - 1];

        // Si el último evento tuvo éxito
        if (lastEvent.success === true) {
          return <Tag color="success" icon={<CheckCircleOutlined />}>Éxito</Tag>;
        }

        // Si el último evento falló, mostrar la razón específica
        if (lastEvent.errorDetails?.reason) {
          const reasonLabel = getReasonLabel(lastEvent.errorDetails.reason);
          return (
            <Tooltip title={lastEvent.error || 'Ver detalles en eventos'}>
              <Tag color={reasonLabel.color} icon={<WarningOutlined />}>
                {reasonLabel.text}
              </Tag>
            </Tooltip>
          );
        }

        // Si no hay razón específica pero hay error
        return (
          <Tooltip title={lastEvent.error || 'Error desconocido'}>
            <Tag color="error" icon={<ExclamationCircleOutlined />}>
              Error
            </Tag>
          </Tooltip>
        );
      }
    }
  ];

  const eventExpandedRowRender = (request: UserRequest) => {
    if (!request.events || request.events.length === 0) {
      return <Empty description="Sin eventos de notificación" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
      <div style={{ padding: '12px', background: '#fafafa' }}>
        <Text strong>Eventos de Notificación:</Text>
        <div style={{ marginTop: 8 }}>
          {request.events.map((event, idx) => (
            <Card key={idx} size="small" style={{ marginBottom: 8 }}>
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="Servicio">{event.service}</Descriptions.Item>
                <Descriptions.Item label="Tipo">{event.type}</Descriptions.Item>
                <Descriptions.Item label="Estado">
                  {event.success ?
                    <Tag color="success" icon={<CheckCircleOutlined />}>Éxito</Tag> :
                    <Tag color="error" icon={<ExclamationCircleOutlined />}>Fallo</Tag>
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Timestamp">
                  {dayjs(event.timestamp).format("DD/MM/YYYY HH:mm:ss")}
                </Descriptions.Item>

                {/* Error básico */}
                {event.error && (
                  <Descriptions.Item label="Error" span={2}>
                    <Text type="danger" code>{event.error}</Text>
                  </Descriptions.Item>
                )}

                {/* Razón del error (destacado) */}
                {event.errorDetails?.reason && (
                  <Descriptions.Item label="Motivo" span={2}>
                    <Tag color={getReasonLabel(event.errorDetails.reason).color} style={{ fontSize: 12, padding: '4px 12px' }}>
                      {getReasonLabel(event.errorDetails.reason).text}
                    </Tag>
                  </Descriptions.Item>
                )}

                {/* Detalles completos del error */}
                {event.errorDetails && (
                  <>
                    {event.errorDetails.statusCode && (
                      <Descriptions.Item label="Status Code" span={2}>
                        <Tag color="red">{event.errorDetails.statusCode} - {event.errorDetails.statusText}</Tag>
                      </Descriptions.Item>
                    )}
                    {event.errorDetails.requestUrl && (
                      <Descriptions.Item label="URL del Webhook" span={2}>
                        <Text code style={{ fontSize: 11 }}>{event.errorDetails.requestUrl}</Text>
                      </Descriptions.Item>
                    )}
                    {event.errorDetails.requestPayload && (
                      <Descriptions.Item label="Payload Enviado" span={2}>
                        <pre style={{ fontSize: 10, margin: 0, maxHeight: 150, overflow: 'auto', background: '#fff', padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                          {JSON.stringify(event.errorDetails.requestPayload, null, 2)}
                        </pre>
                      </Descriptions.Item>
                    )}
                    {event.errorDetails.responseData && (
                      <Descriptions.Item label="Respuesta del Servidor" span={2}>
                        <pre style={{ fontSize: 10, margin: 0, maxHeight: 150, overflow: 'auto', background: '#fff', padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                          {JSON.stringify(event.errorDetails.responseData, null, 2)}
                        </pre>
                      </Descriptions.Item>
                    )}
                    {event.errorDetails.responseHeaders && (
                      <Descriptions.Item label="Headers de Respuesta" span={2}>
                        <pre style={{ fontSize: 10, margin: 0, maxHeight: 100, overflow: 'auto', background: '#fff', padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                          {JSON.stringify(event.errorDetails.responseHeaders, null, 2)}
                        </pre>
                      </Descriptions.Item>
                    )}
                    {event.errorDetails.requestHeaders && (
                      <Descriptions.Item label="Headers Enviados" span={2}>
                        <pre style={{ fontSize: 10, margin: 0, maxHeight: 100, overflow: 'auto', background: '#fff', padding: 8, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                          {JSON.stringify(event.errorDetails.requestHeaders, null, 2)}
                        </pre>
                      </Descriptions.Item>
                    )}
                    {event.errorDetails.axiosError && (
                      <Descriptions.Item label="Error de Axios" span={2}>
                        <Text type="danger" code style={{ fontSize: 11 }}>{event.errorDetails.axiosError}</Text>
                      </Descriptions.Item>
                    )}
                    {event.errorDetails.stack && (
                      <Descriptions.Item label="Stack Trace" span={2}>
                        <pre style={{ fontSize: 9, margin: 0, maxHeight: 200, overflow: 'auto', background: '#fff', padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, color: '#cf1322' }}>
                          {event.errorDetails.stack}
                        </pre>
                      </Descriptions.Item>
                    )}
                  </>
                )}

                {event.response && (
                  <Descriptions.Item label="Respuesta Completa" span={2}>
                    <pre style={{ fontSize: 11, margin: 0, maxHeight: 100, overflow: 'auto' }}>
                      {JSON.stringify(event.response, null, 2)}
                    </pre>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <Space style={{ marginBottom: 8 }}>
        <Text>Filtrar:</Text>
        <Select
          value={expandedFilter}
          onChange={setExpandedFilter}
          style={{ width: 150 }}
          size="small"
        >
          <Option value="all">Todas</Option>
          <Option value="notNotified">No Notificadas</Option>
          <Option value="success">Éxito</Option>
          <Option value="error">Con Error</Option>
        </Select>
      </Space>
      <Table
        columns={requestColumns}
        dataSource={filteredUserRequests}
        rowKey="id"
        pagination={{
          current: expandedPage,
          pageSize: expandedPageSize,
          total: filteredUserRequests.length,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
          pageSizeOptions: ['5', '10', '20'],
          onChange: (page, pageSize) => {
            setExpandedPage(page);
            if (pageSize) setExpandedPageSize(pageSize);
          }
        }}
        expandable={{
          expandedRowRender: eventExpandedRowRender,
          rowExpandable: (request) => request.events && request.events.length > 0
        }}
        size="small"
      />
    </div>
  );
};

export const FacebookAnalytics: React.FC<FacebookAnalyticsProps> = ({ loading: propLoading }) => {
  const [data, setData] = useState<FacebookAnalyticsData | null>(null);
  const [userAnalytics, setUserAnalytics] = useState<UserAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [recentPagination, setRecentPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [userPagination, setUserPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Filtros
  const [recentFilter, setRecentFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");

  // Selección múltiple y notificación
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loadingBulkResend, setLoadingBulkResend] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [resultsModalVisible, setResultsModalVisible] = useState(false);
  const [bulkResults, setBulkResults] = useState<any>(null);

  // Edición de code
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [codeValue, setCodeValue] = useState<string>("");
  const [savingCode, setSavingCode] = useState(false);

  // Edición de userId
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [userIdValue, setUserIdValue] = useState<string>("");
  const [savingUserId, setSavingUserId] = useState(false);

  // Modal de gestión de notificaciones (48 horas)
  const [modalVisible, setModalVisible] = useState(false);
  const [modal48HoursData, setModal48HoursData] = useState<RecentRequest[]>([]);

  // Helper para determinar el período de tiempo basado en el filtro
  const getTimePeriodText = (): string => {
    const start = dayjs(filters.dateRange.start);
    const end = dayjs(filters.dateRange.end);
    const today = dayjs().startOf('day');
    const yesterday = dayjs().subtract(1, 'day').startOf('day');

    // Verificar si es "Hoy"
    if (start.isSame(today, 'day') && end.isSame(today, 'day')) {
      return "hoy";
    }

    // Verificar si es "Ayer"
    if (start.isSame(yesterday, 'day') && end.isSame(yesterday, 'day')) {
      return "ayer";
    }

    // Verificar si es "Últimos 7 días"
    const last7DaysStart = dayjs().subtract(7, 'days').startOf('day');
    if (start.isSame(last7DaysStart, 'day') && end.isSame(today, 'day')) {
      return "en los últimos 7 días";
    }

    // Verificar si es "Últimos 30 días"
    const last30DaysStart = dayjs().subtract(30, 'days').startOf('day');
    if (start.isSame(last30DaysStart, 'day') && end.isSame(today, 'day')) {
      return "en los últimos 30 días";
    }

    // Si no coincide con ningún preset, mostrar rango personalizado
    return `desde ${start.format('DD/MM/YYYY')} hasta ${end.format('DD/MM/YYYY')}`;
  };
  const [loadingModal, setLoadingModal] = useState(false);
  const [modalFilter, setModalFilter] = useState<string>("all");
  const [modalPagination, setModalPagination] = useState({ current: 1, pageSize: 20 });

  const { filters } = useAnalytics();
  const userRole = getAndSanitize("role");

  const fetchFacebookAnalytics = useCallback(async (attemptNumber: number = 0): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const token = getAndSanitize("token");
      let adminId = getAndSanitize("adminId") || getAndSanitize("userId");

      if (!token) {
        throw new Error("Token no disponible");
      }

      if (userRole === "SUPERADMIN") {
        adminId = "all";
      } else if (!adminId) {
        throw new Error("AdminId no disponible");
      }

      const params = new URLSearchParams();
      params.append("adminId", adminId);
      // Paginación real en backend
      params.append("page", recentPagination.current.toString());
      params.append("limit", recentPagination.pageSize.toString());

      // Usar dateRange del contexto (del sidebar de Analytics)
      if (filters.dateRange.start && filters.dateRange.end) {
        params.append("dateRange[start]", filters.dateRange.start);
        params.append("dateRange[end]", filters.dateRange.end);
      }

      if (filters.accountName) {
        params.append("accountName", filters.accountName);
      }

      // Filtro de notificación
      if (recentFilter && recentFilter !== "all") {
        params.append("notificationFilter", recentFilter);
      }



      const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/facebook?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Admin-ID": adminId
        },
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error("[FacebookAnalytics] ❌ Error response:", errorText);
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setData(result.data);
        // Actualizar paginación con el total del backend
        if (result.data.pagination) {
          setRecentPagination(prev => ({
            ...prev,
            total: result.data.pagination.total
          }));
        }
        setRetryAttempt(0);
      } else {
        throw new Error(result.message || "Error al obtener datos");
      }
    } catch (err: any) {
      console.error(`[FacebookAnalytics] 🚨 Error on attempt ${attemptNumber + 1}:`, err);

      if (attemptNumber < CONFIG.RETRY_ATTEMPTS - 1) {
        setRetryAttempt(attemptNumber + 1);
        setTimeout(() => {
          fetchFacebookAnalytics(attemptNumber + 1);
        }, CONFIG.RETRY_DELAY);
        return;
      }

      setError(err.message || "Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  }, [filters, userRole, recentPagination.current, recentPagination.pageSize, recentFilter]);

  const fetchUserAnalytics = useCallback(async (): Promise<void> => {
    try {
      const token = getAndSanitize("token");
      let adminId = getAndSanitize("adminId") || getAndSanitize("userId");

      if (!token) return;

      if (userRole === "SUPERADMIN") {
        adminId = "all";
      }

      const params = new URLSearchParams();
      params.append("adminId", adminId);
      // Paginación real en backend
      params.append("page", userPagination.current.toString());
      params.append("limit", userPagination.pageSize.toString());

      // Usar dateRange del contexto (del sidebar de Analytics)
      if (filters.dateRange.start && filters.dateRange.end) {
        params.append("dateRange[start]", filters.dateRange.start);
        params.append("dateRange[end]", filters.dateRange.end);
      }

      if (filters.accountName) {
        params.append("accountName", filters.accountName);
      }

      if (searchTerm) {
        params.append("search", searchTerm);
      }

      // Filtro de notificación para usuarios
      if (userFilter && userFilter !== "all") {
        params.append("notificationFilter", userFilter);
      }


      const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/facebook/by-user?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Admin-ID": adminId
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setUserAnalytics(result.data);
          // Actualizar el total del backend
          setUserPagination(prev => ({
            ...prev,
            total: result.data.pagination.total
          }));
        }
      }
    } catch (err) {
      console.error("[FacebookAnalytics] Error fetching user analytics:", err);
    }
  }, [filters, searchTerm, userRole, userPagination.current, userPagination.pageSize, userFilter]);

  useEffect(() => {
    fetchFacebookAnalytics();
    fetchUserAnalytics();

    const interval = setInterval(() => {
      fetchFacebookAnalytics();
      fetchUserAnalytics();
    }, CONFIG.REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchFacebookAnalytics, fetchUserAnalytics]);

  const handleRetry = useCallback(() => {
    setRetryAttempt(0);
    fetchFacebookAnalytics(0);
    fetchUserAnalytics();
  }, [fetchFacebookAnalytics, fetchUserAnalytics]);

  const handleRecentPageChange = useCallback((page: number, pageSize?: number) => {
    setRecentPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize
    }));
  }, []);

  const handleUserPageChange = useCallback((page: number, pageSize?: number) => {
    setUserPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize
    }));
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setUserPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  const handleUserFilterChange = useCallback((value: string) => {
    setUserFilter(value);
    setUserPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  // Actualizar userId de usuario
  const handleUpdateUserId = useCallback(async (panelUserId: string, newUserId: string) => {
    try {
      setSavingUserId(true);
      const token = getAndSanitize("token");

      const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/facebook/update-userid/${panelUserId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId: newUserId })
      });

      const result = await response.json();

      if (result.success) {
        // Actualizar SOLO los datos del modal (optimistic update)
        setModal48HoursData(prev =>
          prev.map(req =>
            req.panelUserId === panelUserId
              ? { ...req, userId: newUserId }
              : req
          )
        );

        // Actualizar también los datos principales sin recargar
        if (data?.recentRequests) {
          setData(prevData => prevData ? {
            ...prevData,
            recentRequests: prevData.recentRequests.map(req =>
              req.panelUserId === panelUserId
                ? { ...req, userId: newUserId }
                : req
            )
          } : null);
        }

        setEditingUserId(null);
        setUserIdValue("");
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error("Error updating userId:", error);
      alert("Error al actualizar el userId");
    } finally {
      setSavingUserId(false);
    }
  }, [data]);

  // Actualizar code de usuario
  const handleUpdateCode = useCallback(async (panelUserId: string, newCode: string) => {
    try {
      setSavingCode(true);
      const token = getAndSanitize("token");

      const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/facebook/update-code/${panelUserId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: newCode })
      });

      const result = await response.json();

      if (result.success) {
        // Actualizar SOLO los datos del modal (optimistic update)
        setModal48HoursData(prev =>
          prev.map(req =>
            req.panelUserId === panelUserId
              ? { ...req, userCode: newCode }
              : req
          )
        );

        // Actualizar también los datos principales sin recargar
        if (data?.recentRequests) {
          setData(prevData => prevData ? {
            ...prevData,
            recentRequests: prevData.recentRequests.map(req =>
              req.panelUserId === panelUserId
                ? { ...req, userCode: newCode }
                : req
            )
          } : null);
        }

        setEditingCode(null);
        setCodeValue("");
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error("Error updating code:", error);
      alert("Error al actualizar el código");
    } finally {
      setSavingCode(false);
    }
  }, [data]);

  // Mostrar modal de confirmación
  const handleShowConfirmation = useCallback(() => {
    if (selectedRowKeys.length === 0) {
      alert("Selecciona al menos una request");
      return;
    }
    setConfirmModalVisible(true);
  }, [selectedRowKeys]);

  // Reenviar webhooks en bulk (después de confirmar)
  const handleConfirmBulkResend = useCallback(async () => {
    try {
      setConfirmModalVisible(false); // Cerrar modal de confirmación
      setLoadingBulkResend(true);
      const token = getAndSanitize("token");

      const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/facebook/resend-webhooks-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ requestIds: selectedRowKeys })
      });

      const result = await response.json();

      if (result.success) {
        // Guardar resultados completos
        setBulkResults(result.data);

        // Recargar datos del modal
        const now = dayjs();
        const fortyEightHoursAgo = now.subtract(48, 'hours');
        const params = new URLSearchParams();
        let adminId = getAndSanitize("adminId") || getAndSanitize("userId");
        if (userRole === "SUPERADMIN") adminId = "all";

        params.append("adminId", adminId);
        params.append("page", "1");
        params.append("limit", "1000");
        params.append("dateRange[start]", fortyEightHoursAgo.format("YYYY-MM-DD"));
        params.append("dateRange[end]", now.format("YYYY-MM-DD"));
        if (filters.accountName) params.append("accountName", filters.accountName);

        const refreshResponse = await fetch(
          `${ANALYTICS_API_URL}/api/analytics/facebook?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (refreshResponse.ok) {
          const refreshResult = await refreshResponse.json();
          if (refreshResult.success && refreshResult.data) {
            setModal48HoursData(refreshResult.data.recentRequests || []);
          }
        }

        // Mostrar modal de resultados en lugar de alert
        setResultsModalVisible(true);
        setSelectedRowKeys([]);
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error("Error bulk resending webhooks:", error);
      alert("Error al reenviar webhooks en bulk");
    } finally {
      setLoadingBulkResend(false);
    }
  }, [selectedRowKeys, userRole, filters.accountName]);

  // Abrir modal y cargar datos de últimas 48 horas
  const handleOpenModal = useCallback(async () => {
    try {
      setLoadingModal(true);
      setModalVisible(true);

      const token = getAndSanitize("token");
      let adminId = getAndSanitize("adminId") || getAndSanitize("userId");

      if (!token) return;

      if (userRole === "SUPERADMIN") {
        adminId = "all";
      }

      const params = new URLSearchParams();
      params.append("adminId", adminId);
      params.append("page", "1");
      params.append("limit", "1000"); // Traer todas las de 48 horas

      // FILTRO DE 48 HORAS para el modal
      const now = dayjs();
      const fortyEightHoursAgo = now.subtract(48, 'hours');
      params.append("dateRange[start]", fortyEightHoursAgo.format("YYYY-MM-DD"));
      params.append("dateRange[end]", now.format("YYYY-MM-DD"));

      if (filters.accountName) {
        params.append("accountName", filters.accountName);
      }

      const response = await fetch(
        `${ANALYTICS_API_URL}/api/analytics/facebook?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setModal48HoursData(result.data.recentRequests || []);
        }
      }
    } catch (error) {
      console.error("Error loading 48 hours data:", error);
    } finally {
      setLoadingModal(false);
    }
  }, [filters.accountName, userRole]);

  // Cerrar modal
  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setSelectedRowKeys([]);
    setEditingCode(null);
    setCodeValue("");
    setEditingUserId(null);
    setUserIdValue("");
    setModalFilter("all");
    setModalPagination({ current: 1, pageSize: 20 });
  }, []);

  // Sin filtrado local - todo viene del backend

  // Columnas para tabla de usuarios
  const userColumns = useMemo(() => [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
      render: (text: string, record: UserAnalytics) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.code && <Text code style={{ fontSize: 12 }}>{record.code}</Text>}
        </Space>
      )
    },
    {
      title: 'Cuenta',
      dataIndex: 'accountName',
      key: 'accountName',
    },
    {
      title: 'Cargas',
      dataIndex: 'totalRequests',
      key: 'totalRequests',
      sorter: (a: UserAnalytics, b: UserAnalytics) => a.totalRequests - b.totalRequests,
    },
    {
      title: 'Notificadas',
      dataIndex: 'notifiedRequests',
      key: 'notifiedRequests',
      render: (value: number) => <Tag color="success">{value}</Tag>
    },
    {
      title: 'Fallidas',
      dataIndex: 'failedNotifications',
      key: 'failedNotifications',
      render: (value: number) => <Tag color="error">{value}</Tag>
    },
    {
      title: 'Tasa de Éxito',
      dataIndex: 'successRate',
      key: 'successRate',
      render: (rate: number) => <Progress percent={Number(rate.toFixed(1))} size="small" />
    },
    {
      title: 'Monto Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number | null) => amount != null ? `$${amount.toLocaleString()}` : '-'
    }
  ], []);

  // Columnas para tabla de requests recientes (TABLA PRINCIPAL - SIN EDICION NI NOTIFICACION)
  const recentRequestsColumns = useMemo(() => [
    {
      title: 'Solicitud',
      dataIndex: 'requestNumber',
      key: 'requestNumber',
      render: (text: string) => <Text code>{text}</Text>
    },
    {
      title: 'Usuario / ID / Cuenta',
      key: 'userInfo',
      render: (_: any, record: RecentRequest) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.username || 'Sin nombre'}</Text>
          {record.userId && <Text code style={{ fontSize: 11 }}>ID: {record.userId}</Text>}
          {record.accountName && <Text type="secondary" style={{ fontSize: 11 }}>{record.accountName}</Text>}
        </Space>
      )
    },
    {
      title: 'Código',
      dataIndex: 'userCode',
      key: 'userCode',
      render: (text: string) => text ? <Text code>{text}</Text> : <Text type="secondary" italic>Sin código</Text>
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number | null) => amount != null ? `$${amount.toLocaleString()}` : '-'
    },
    {
      title: 'Categoría',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (eventType?: 'FirstPurchase' | 'SecondPurchase' | 'RepeatCustomer') => {
        const label = getEventTypeLabel(eventType);
        return (
          <Tag color={label.color}>
            {label.icon} {label.text}
          </Tag>
        );
      }
    },
    {
      title: 'Notificación',
      key: 'notification',
      render: (record: RecentRequest) => {
        // NUEVO: Si tuvo éxito, mostrar éxito
        if (record.wasNotified && record.notificationSuccess) {
          return <Tag color="success" icon={<CheckCircleOutlined />}>Éxito</Tag>;
        }

        // NUEVO: SIEMPRE debe haber razón de error si no tuvo éxito
        if (record.notificationErrorDetails?.reason) {
          const reasonLabel = getReasonLabel(record.notificationErrorDetails.reason);

          const errorContent = (
            <div style={{ maxWidth: 600, maxHeight: 500, overflow: 'auto' }}>
              <Descriptions size="small" column={1} bordered>
                {record.notificationError && (
                  <Descriptions.Item label="Error">
                    <Text type="danger" code style={{ fontSize: 11 }}>{record.notificationError}</Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Motivo">
                  <Tag color={reasonLabel.color} style={{ fontSize: 12 }}>
                    {reasonLabel.text}
                  </Tag>
                </Descriptions.Item>
                {Object.entries(record.notificationErrorDetails)
                  .filter(([key]) => key !== 'reason')
                  .map(([key, value]) => (
                    value != null && (
                      <Descriptions.Item label={key} key={key}>
                        <Text code style={{ fontSize: 10 }}>
                          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                        </Text>
                      </Descriptions.Item>
                    )
                  ))}
              </Descriptions>
            </div>
          );

          return (
            <Popover
              content={errorContent}
              title={
                <Space>
                  <WarningOutlined style={{ color: '#faad14' }} />
                  <Text strong>Detalles del Error</Text>
                </Space>
              }
              trigger="click"
              placement="left"
            >
              <Tag color={reasonLabel.color} icon={<WarningOutlined />} style={{ cursor: 'pointer' }}>
                {reasonLabel.text}
              </Tag>
            </Popover>
          );
        }

        // NUEVO: Si no hay errorDetails específicos pero tiene error
        if (record.notificationError) {
          return (
            <Tooltip title={record.notificationError}>
              <Tag color="error" icon={<ExclamationCircleOutlined />}>
                Error (sin detalles)
              </Tag>
            </Tooltip>
          );
        }

        // NUEVO: Si llegamos aquí, es un error del sistema (no debería pasar)
        return (
          <Tag color="warning" icon={<WarningOutlined />}>
            Sin Info de Notificación
          </Tag>
        );
      }
    },
    ...(data?.appliedFilters?.includeAllAdmins || userRole === "SUPERADMIN" ? [{
      title: 'Admin',
      dataIndex: 'adminId',
      key: 'adminId',
      render: (adminId: string) => <Text code style={{ fontSize: 11 }}>{adminId?.slice(0, 8)}...</Text>
    }] : []),
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format("DD/MM/YYYY HH:mm")
    }
  ], [data?.appliedFilters?.includeAllAdmins, userRole]);

  // Columnas para modal de 48 horas (CON EDICIÓN Y NOTIFICACIÓN)
  const modal48HoursColumns = useMemo(() => [
    {
      title: 'Solicitud',
      dataIndex: 'requestNumber',
      key: 'requestNumber',
      render: (text: string) => <Text code>{text}</Text>
    },
    {
      title: 'Categoría',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (eventType?: 'FirstPurchase' | 'SecondPurchase' | 'RepeatCustomer') => {
        const label = getEventTypeLabel(eventType);
        return (
          <Tag color={label.color}>
            {label.icon} {label.text}
          </Tag>
        );
      }
    },
    {
      title: 'Usuario / ID / Cuenta',
      key: 'userInfo',
      render: (_: any, record: RecentRequest) => {
        const isEditingUserId = editingUserId === record.id;

        return (
          <Space direction="vertical" size={0}>
            <Text strong>{record.username || 'Sin nombre'}</Text>

            {/* UserID editable */}
            {isEditingUserId ? (
              <Space.Compact size="small">
                <Input
                  size="small"
                  value={userIdValue}
                  onChange={(e) => setUserIdValue(e.target.value)}
                  onPressEnter={() => handleUpdateUserId(record.panelUserId, userIdValue)}
                  placeholder="User ID"
                  style={{ width: 100, fontSize: 11 }}
                  autoFocus
                />
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleUpdateUserId(record.panelUserId, userIdValue)}
                  loading={savingUserId}
                />
                <Button
                  size="small"
                  onClick={() => {
                    setEditingUserId(null);
                    setUserIdValue("");
                  }}
                >
                  ✕
                </Button>
              </Space.Compact>
            ) : (
              <Space size={4}>
                {record.userId ? (
                  <Text code style={{ fontSize: 11 }}>ID: {record.userId}</Text>
                ) : (
                  <Text type="secondary" italic style={{ fontSize: 11 }}>Sin ID</Text>
                )}
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingUserId(record.id);
                    setUserIdValue(record.userId || "");
                  }}
                  style={{ padding: 0, height: 16 }}
                />
              </Space>
            )}

            {record.accountName && <Text type="secondary" style={{ fontSize: 11 }}>{record.accountName}</Text>}
          </Space>
        );
      }
    },
    {
      title: 'Código',
      dataIndex: 'userCode',
      key: 'userCode',
      render: (userCode: string, record: RecentRequest) => {
        const isEditing = editingCode === record.id;

        if (isEditing) {
          return (
            <Space.Compact>
              <Input
                size="small"
                value={codeValue}
                onChange={(e) => setCodeValue(e.target.value)}
                onPressEnter={() => handleUpdateCode(record.panelUserId, codeValue)}
                autoFocus
                style={{ width: 120 }}
              />
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => handleUpdateCode(record.panelUserId, codeValue)}
                loading={savingCode}
              />
              <Button
                size="small"
                onClick={() => {
                  setEditingCode(null);
                  setCodeValue("");
                }}
              >
                ✕
              </Button>
            </Space.Compact>
          );
        }

        return (
          <Space>
            {userCode ? (
              <Text code>{userCode}</Text>
            ) : (
              <Text type="secondary" italic>Sin código</Text>
            )}
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingCode(record.id);
                setCodeValue(userCode || "");
              }}
            />
          </Space>
        );
      }
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number | null) => amount != null ? `$${amount.toLocaleString()}` : '-'
    },
    {
      title: 'Notificación',
      key: 'notification',
      render: (record: RecentRequest) => {
        // NUEVO: Si tuvo éxito
        if (record.wasNotified && record.notificationSuccess) {
          return <Tag color="success" icon={<CheckCircleOutlined />}>Éxito</Tag>;
        }

        // NUEVO: Si hay razón de error específica
        if (record.notificationErrorDetails?.reason) {
          const reasonLabel = getReasonLabel(record.notificationErrorDetails.reason);
          return (
            <Tooltip title={record.notificationError || 'Ver detalles'}>
              <Tag color={reasonLabel.color} icon={<WarningOutlined />}>
                {reasonLabel.text}
              </Tag>
            </Tooltip>
          );
        }

        // NUEVO: Fallback para errores sin detalle
        if (record.notificationError) {
          return (
            <Tooltip title={record.notificationError}>
              <Tag color="error" icon={<ExclamationCircleOutlined />}>
                Error
              </Tag>
            </Tooltip>
          );
        }

        // NUEVO: Sin información (no debería pasar)
        return (
          <Tag color="warning" icon={<WarningOutlined />}>
            Sin Info
          </Tag>
        );
      }
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format("DD/MM/YYYY HH:mm")
    }
  ], [editingCode, codeValue, savingCode, handleUpdateCode, editingUserId, userIdValue, savingUserId, handleUpdateUserId]);

  // Filtrar datos del modal
  const filteredModalData = useMemo(() => {
    if (modalFilter === "all") return modal48HoursData;

    return modal48HoursData.filter(request => {
      switch (modalFilter) {
        case "notNotified":
          return !request.wasNotified;
        case "success":
          return request.wasNotified && request.notificationSuccess;
        case "error":
          return request.wasNotified && !request.notificationSuccess;
        default:
          return true;
      }
    });
  }, [modal48HoursData, modalFilter]);

  // Renderizar tabla expandida con cargas y eventos CON PAGINACIÓN
  const expandedRowRender = useCallback((record: UserAnalytics) => {
    return <ExpandedUserRequests record={record} />;
  }, []);

  // Popover de errores
  const errorAnalysisContent = useMemo(() => {
    if (!data || data.errorAnalysis.totalErrors === 0) return null;

    return (
      <div style={{ maxWidth: 400, maxHeight: 400, overflow: 'auto' }}>
        <Text strong>Errores más frecuentes:</Text>
        <List
          size="small"
          style={{ marginTop: 8 }}
          dataSource={Object.entries(data.errorAnalysis.errorFrequency)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)}
          renderItem={([error, count]) => (
            <List.Item>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text code style={{ color: '#ff4d4f', fontSize: 11 }}>{error}</Text>
                <Tag color="red">{count}</Tag>
              </Space>
            </List.Item>
          )}
        />
      </div>
    );
  }, [data]);

  if (loading || propLoading) {
    return (
      <div style={{ padding: '16px' }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[1, 2, 3, 4].map(i => (
            <Col xs={24} sm={12} md={8} lg={6} key={i}>
              <Card size="small">
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Col>
          ))}
        </Row>
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar estadísticas"
        description={
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text>{error}</Text>
            {retryAttempt > 0 && (
              <Text type="secondary">
                Reintentando automáticamente... (Intento {retryAttempt}/{CONFIG.RETRY_ATTEMPTS})
              </Text>
            )}
          </Space>
        }
        type="error"
        showIcon
        icon={<ExclamationCircleOutlined />}
        action={
          <Button
            onClick={handleRetry}
            icon={<ReloadOutlined />}
            size="small"
            type="primary"
            danger
            disabled={loading}
          >
            Reintentar
          </Button>
        }
      />
    );
  }

  if (!data) {
    return (
      <Alert
        message="Sin datos"
        description="No hay estadísticas disponibles para el período seleccionado"
        type="info"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: "0" }}>
      {/* Botón para abrir modal de gestión de notificaciones (48 horas) */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          size="large"
          icon={<BellOutlined />}
          onClick={handleOpenModal}
        >
          Gestión de Notificaciones (Últimas 48 Horas)
        </Button>
      </div>

      {/* Estadísticas de usuarios creados en el período filtrado */}
      {data.todayStats && (
        <Card style={{ marginBottom: 16, background: '#f0f7ff', borderColor: '#1890ff' }}>
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
              📊 Del total de usuarios creados {getTimePeriodText()} ({data.todayStats.usersCreatedToday}):
            </Text>
            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Se notificaron"
                  value={data.todayStats.usersNotifiedToday}
                  prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                  valueStyle={{ color: "#52c41a", fontSize: 20 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="No notificaron"
                  value={data.todayStats.usersNotNotifiedToday}
                  prefix={<BellOutlined style={{ color: "#faad14" }} />}
                  valueStyle={{ color: "#faad14", fontSize: 20 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title="Con error"
                  value={data.todayStats.usersErrorToday}
                  prefix={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
                  valueStyle={{ color: "#ff4d4f", fontSize: 20 }}
                />
              </Col>
            </Row>
          </Space>
        </Card>
      )}

      {/* Estadísticas principales CON POPOVER DE ERRORES */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total de Cargas"
              value={data.summary.totalRequests}
              prefix={<NumberOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Notificadas"
              value={data.summary.notifiedRequests}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
              suffix={`(${data.summary.notificationRate.toFixed(1)}%)`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Statistic
                title="Fallidas"
                value={data.summary.failedNotifications}
                prefix={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
                valueStyle={{ color: "#ff4d4f" }}
              />
              {data.errorAnalysis.totalErrors > 0 && (
                <Popover
                  content={errorAnalysisContent}
                  title={
                    <Space>
                      <WarningOutlined style={{ color: '#ff4d4f' }} />
                      <Text strong>Análisis de Errores</Text>
                    </Space>
                  }
                  trigger="click"
                  placement="bottomLeft"
                >
                  <Button
                    size="small"
                    type="link"
                    danger
                    icon={<WarningOutlined />}
                    style={{ padding: 0 }}
                  >
                    {data.errorAnalysis.uniqueErrors.length} tipos de error
                  </Button>
                </Popover>
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Monto Notificado"
              value={data.summary.notifiedAmount}
              prefix={<DollarOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
              suffix={`(${data.summary.amountRate.toFixed(1)}%)`}
            />
          </Card>
        </Col>
      </Row>

      {/* Layout con Sidebar A LA DERECHA */}
      <Layout style={{ background: 'transparent' }}>
        {/* Contenido principal */}
        <Content style={{ marginRight: sidebarCollapsed ? 0 : 16 }}>
          {/* Búsqueda */}
          <Card style={{ marginBottom: 16 }}>
            <Input.Search
              placeholder="Buscar por usuario o código..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Card>

          {/* Tabla expandible de usuarios CON FILTROS */}
          <Card
            title="👥 Análisis Detallado por Usuario"
            style={{ marginBottom: 16 }}
            extra={
              <Space>
                <Text>Filtrar:</Text>
                <Select
                  value={userFilter}
                  onChange={handleUserFilterChange}
                  style={{ width: 150 }}
                  size="small"
                >
                  <Option value="all">Todos</Option>
                  <Option value="notNotified">No Notificados</Option>
                  <Option value="success">Solo Éxito</Option>
                  <Option value="error">Con Errores</Option>
                </Select>
              </Space>
            }
          >
            {userAnalytics ? (
              <Table
                columns={userColumns}
                dataSource={userAnalytics.users || []}
                rowKey="userId"
                expandable={{
                  expandedRowRender,
                  rowExpandable: (record) => record.requests && record.requests.length > 0
                }}
                pagination={{
                  current: userPagination.current,
                  pageSize: userPagination.pageSize,
                  total: userPagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} usuarios`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  onChange: handleUserPageChange,
                  onShowSizeChange: handleUserPageChange
                }}
                size="small"
              />
            ) : (
              <Skeleton active />
            )}
          </Card>

          <Divider />

          {/* Tabla de Todas las Requests */}
          <Card
            title="📋 Todas las Requests"
            extra={
              <Space>
                <Text>Filtrar:</Text>
                <Select
                  value={recentFilter}
                  onChange={(value) => {
                    setRecentFilter(value);
                    setRecentPagination(prev => ({ ...prev, current: 1 }));
                  }}
                  style={{ width: 150 }}
                  size="small"
                >
                  <Option value="all">Todos</Option>
                  <Option value="notNotified">No Notificados</Option>
                  <Option value="success">Solo Éxito</Option>
                  <Option value="error">Con Errores</Option>
                </Select>
              </Space>
            }
          >
            <Table
              dataSource={data?.recentRequests || []}
              columns={recentRequestsColumns}
              rowKey="id"
              pagination={{
                current: recentPagination.current,
                pageSize: recentPagination.pageSize,
                total: recentPagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} requests`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: handleRecentPageChange,
                onShowSizeChange: handleRecentPageChange
              }}
              size="small"
            />
          </Card>
        </Content>

        {/* Sidebar con breakdowns A LA DERECHA CON SCROLL */}
        <Sider
          width={280}
          collapsed={sidebarCollapsed}
          collapsedWidth={0}
          breakpoint="lg"
          onCollapse={setSidebarCollapsed}
          style={{ background: 'transparent' }}
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* NUEVO: Desglose por EventType */}
            {data.eventTypeBreakdown && data.eventTypeBreakdown.length > 0 && (
              <Card
                title={<Space><BarChartOutlined /> Por Categoría de Usuario</Space>}
                size="small"
                style={{ marginBottom: 0 }}
              >
                <List
                  size="small"
                  dataSource={data.eventTypeBreakdown}
                  renderItem={(item) => {
                    const label = getEventTypeLabel(item.eventType);
                    return (
                      <List.Item style={{ padding: '8px 0' }}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Space>
                              <span>{label.icon}</span>
                              <Text strong>{label.text}</Text>
                            </Space>
                            <Tag color={label.color}>{item.count}</Tag>
                          </div>
                          <Progress
                            percent={item.notificationRate}
                            size="small"
                            format={(percent) => `${percent?.toFixed(0)}% éxito`}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: 11 }}>
                            <Text type="secondary">✓ {item.notified}</Text>
                            <Text type="danger">✗ {item.failed}</Text>
                            <Text type="secondary">${item.totalAmount.toLocaleString()}</Text>
                          </div>
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              </Card>
            )}

            {/* Breakdown por cuenta - CON SCROLL */}
            <Card
              title={<Space><BarChartOutlined /> Por Cuenta</Space>}
              size="small"
              style={{ marginBottom: 0 }}
            >
              <List
                size="small"
                dataSource={data.accountBreakdown}
                style={{ maxHeight: 400, overflowY: 'auto' }}
                renderItem={(item) => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Text strong>{item.accountName}</Text>
                        <Tag color="blue">{item.totalRequests} cargas</Tag>
                      </div>
                      <Progress
                        percent={item.totalRequests > 0 ? (item.notifiedRequests / item.totalRequests) * 100 : 0}
                        size="small"
                        format={(percent) => `${percent?.toFixed(0)}% éxito`}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: 11 }}>
                        <Text type="secondary">✓ {item.notifiedRequests}</Text>
                        <Text type="danger">✗ {item.failedNotifications}</Text>
                        <Text type="secondary">${item.totalAmount.toLocaleString()}</Text>
                      </div>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>

            {/* Breakdown por admin - CON SCROLL */}
            {data.adminBreakdown && data.adminBreakdown.length > 0 && (
              <Card
                title={<Space><UserOutlined /> Por Admin</Space>}
                size="small"
              >
                <List
                  size="small"
                  dataSource={data.adminBreakdown}
                  style={{ maxHeight: 400, overflowY: 'auto' }}
                  renderItem={(item) => (
                    <List.Item style={{ padding: '8px 0' }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Text code style={{ fontSize: 11 }}>{item.adminId.slice(0, 12)}...</Text>
                          <Tag color="purple">{item.totalRequests}</Tag>
                        </div>
                        <Progress
                          percent={item.totalRequests > 0 ? (item.notifiedRequests / item.totalRequests) * 100 : 0}
                          size="small"
                          format={(percent) => `${percent?.toFixed(0)}%`}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: 11 }}>
                          <Text type="secondary">✓ {item.notifiedRequests}</Text>
                          <Text type="danger">✗ {item.failedNotifications}</Text>
                        </div>
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </Space>
        </Sider>
      </Layout>

      {/* Toggle sidebar button */}
      <Button
        type="primary"
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          display: sidebarCollapsed ? 'block' : 'none'
        }}
        icon={<BarChartOutlined />}
      >
        Mostrar Breakdowns
      </Button>

      {/* MODAL DE GESTIÓN DE NOTIFICACIONES (48 HORAS) */}
      <Modal
        title={
          <Space>
            <BellOutlined />
            <Text strong>Gestión de Notificaciones - Últimas 48 Horas</Text>
            {selectedRowKeys.length > 0 && (
              <Tag color="blue">{selectedRowKeys.length} seleccionadas</Tag>
            )}
          </Space>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        width="95%"
        style={{ top: 20 }}
        footer={[
          <Space key="footer">
            {selectedRowKeys.length > 0 && (
              <Button
                type="primary"
                icon={<BellOutlined />}
                onClick={handleShowConfirmation}
              >
                Notificar Seleccionadas ({selectedRowKeys.length})
              </Button>
            )}
            <Button onClick={handleCloseModal}>Cerrar</Button>
          </Space>
        ]}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* Filtros y Acciones */}
          <Card size="small">
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <Text strong>Filtrar:</Text>
                <Select
                  value={modalFilter}
                  onChange={setModalFilter}
                  style={{ width: 200 }}
                >
                  <Option value="all">Todas ({modal48HoursData.length})</Option>
                  <Option value="notNotified">
                    No Notificadas ({modal48HoursData.filter(r => !r.wasNotified).length})
                  </Option>
                  <Option value="success">
                    Éxito ({modal48HoursData.filter(r => r.wasNotified && r.notificationSuccess).length})
                  </Option>
                  <Option value="error">
                    Con Error ({modal48HoursData.filter(r => r.wasNotified && !r.notificationSuccess).length})
                  </Option>
                </Select>
                <Text type="secondary">
                  {filteredModalData.length} requests
                </Text>
              </Space>

              {selectedRowKeys.length > 0 && (
                <Button
                  type="primary"
                  icon={<BellOutlined />}
                  onClick={handleShowConfirmation}
                >
                  Notificar Seleccionadas ({selectedRowKeys.length})
                </Button>
              )}
            </Space>
          </Card>

          {/* Tabla */}
          {loadingModal ? (
            <Skeleton active />
          ) : (
            <Table
              dataSource={filteredModalData}
              columns={modal48HoursColumns}
              rowKey="id"
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                getCheckboxProps: (record: RecentRequest) => {
                  const now = dayjs();
                  const fortyEightHoursAgo = now.subtract(48, 'hours');
                  const createdAt = dayjs(record.createdAt);
                  const isWithin48Hours = createdAt.isAfter(fortyEightHoursAgo);
                  const hasCode = !!record.userCode;
                  const hasUserId = !!record.userId;
                  const canResend = isWithin48Hours && hasCode && hasUserId;

                  return {
                    disabled: !canResend,
                  };
                },
              }}
              pagination={{
                current: modalPagination.current,
                pageSize: modalPagination.pageSize,
                onChange: (page, pageSize) => setModalPagination({ current: page, pageSize: pageSize || 20 }),
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              size="small"
              scroll={{ x: 1400 }}
            />
          )}
        </Space>
      </Modal>

      {/* MODAL DE CONFIRMACIÓN PARA NOTIFICACIONES */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <Text strong>Confirmar Notificación</Text>
          </Space>
        }
        open={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        onOk={handleConfirmBulkResend}
        confirmLoading={loadingBulkResend}
        okText="Sí, Notificar"
        cancelText="Cancelar"
        okButtonProps={{ danger: false, type: 'primary' }}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            message="Estás a punto de enviar notificaciones"
            description={
              <Space direction="vertical" size={8}>
                <Text>
                  Se enviarán <Text strong>{selectedRowKeys.length}</Text> notificaciones webhook.
                </Text>
                <Text type="secondary">
                  Solo se procesarán las requests que cumplan con los requisitos:
                </Text>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Tener <Text code>code</Text> asignado</li>
                  <li>Tener <Text code>userId</Text> asignado</li>
                  <li>Estar dentro de las últimas 48 horas</li>
                </ul>
              </Space>
            }
            type="warning"
            showIcon
          />
          <Text strong>¿Deseas continuar?</Text>
        </Space>
      </Modal>

      {/* MODAL DE RESULTADOS DETALLADOS */}
      <Modal
        title={
          <Space>
            {bulkResults?.summary.successful > 0 && bulkResults?.summary.failed === 0 ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : bulkResults?.summary.failed > 0 ? (
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            ) : (
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            )}
            <Text strong>Resultados de Notificación</Text>
          </Space>
        }
        open={resultsModalVisible}
        onCancel={() => {
          setResultsModalVisible(false);
          setBulkResults(null);
        }}
        width={900}
        footer={[
          <Button key="close" type="primary" onClick={() => {
            setResultsModalVisible(false);
            setBulkResults(null);
          }}>
            Cerrar
          </Button>
        ]}
      >
        {bulkResults && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* Resumen */}
            <Card size="small">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Total Procesadas"
                    value={bulkResults.summary.total}
                    prefix={<NumberOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Exitosas"
                    value={bulkResults.summary.successful}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Fallidas"
                    value={bulkResults.summary.failed}
                    valueStyle={{ color: '#ff4d4f' }}
                    prefix={<ExclamationCircleOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            {/* Detalle de cada notificación */}
            <Divider orientation="left">Detalle de Notificaciones</Divider>

            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              <List
                dataSource={bulkResults.results}
                renderItem={(item: any) => (
                  <List.Item>
                    <Card
                      size="small"
                      style={{ width: '100%' }}
                      type={item.success ? undefined : "inner"}
                    >
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {/* Encabezado */}
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Space>
                            <Text strong>Request ID:</Text>
                            <Text code>{item.requestId}</Text>
                          </Space>
                          {item.success ? (
                            <Tag color="success" icon={<CheckCircleOutlined />}>Éxito</Tag>
                          ) : (
                            <Tag color="error" icon={<ExclamationCircleOutlined />}>Error</Tag>
                          )}
                        </Space>

                        {/* Mensaje */}
                        {item.message && (
                          <Text type={item.success ? "success" : "secondary"}>
                            {item.message}
                          </Text>
                        )}

                        {/* Error */}
                        {item.error && (
                          <Alert
                            message="Error"
                            description={<Text code style={{ fontSize: 11 }}>{item.error}</Text>}
                            type="error"
                            showIcon
                            style={{ marginTop: 8 }}
                          />
                        )}

                        {/* Detalles del error */}
                        {item.errorDetails && (
                          <Descriptions size="small" column={1} bordered style={{ marginTop: 8 }}>
                            {item.errorDetails.reason && (
                              <Descriptions.Item label="Motivo">
                                <Tag color={getReasonLabel(item.errorDetails.reason).color}>
                                  {getReasonLabel(item.errorDetails.reason).text}
                                </Tag>
                              </Descriptions.Item>
                            )}
                            {item.errorDetails.statusCode && (
                              <Descriptions.Item label="Status Code">
                                <Tag color="red">
                                  {item.errorDetails.statusCode} - {item.errorDetails.statusText}
                                </Tag>
                              </Descriptions.Item>
                            )}
                            {item.errorDetails.requestUrl && (
                              <Descriptions.Item label="URL">
                                <Text code style={{ fontSize: 10 }}>{item.errorDetails.requestUrl}</Text>
                              </Descriptions.Item>
                            )}
                            {item.errorDetails.responseData && (
                              <Descriptions.Item label="Respuesta del Servidor">
                                <pre style={{ fontSize: 10, margin: 0, maxHeight: 100, overflow: 'auto' }}>
                                  {JSON.stringify(item.errorDetails.responseData, null, 2)}
                                </pre>
                              </Descriptions.Item>
                            )}
                          </Descriptions>
                        )}
                      </Space>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default FacebookAnalytics;
